import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import TopProductsApiService from "@/core/services/api.service.top-products-catalog";

export default {
  name: "selection-groups",
  data() {
    return {
      selectionGroups: [],
      totalGroups: 0,
      totalPages: 0,
      loading: false,
      pagination: {
        page: 1,
        itemsPerPage: 10
      },
      apiInitialized: false,
      createDialog: false,
      editDialog: false,
      confirmDeleteDialog: false,
      groupToDelete: null,
      formData: {
        id: null,
        name: ""
      },
      formValid: false,
      nameRules: [
        v => !!v || "Name is required",
        v => (v && v.length >= 3) || "Name must be at least 3 characters"
      ],
      importDialog: false,
      importGroup: null,
      importFile: null,
      importMode: "append",
      importResult: null,
      importing: false
    };
  },
  computed: {
    pageName() {
      return "Selection Groups";
    },
    headers() {
      return [
        { text: "ID", value: "id", sortable: false, width: "8%" },
        { text: "Name", value: "name", sortable: false, width: "30%" },
        { text: "Items", value: "itemCount", sortable: false, width: "10%" },
        { text: "Created At", value: "createdAt", sortable: false, width: "22%" },
        { text: "Created By (User ID)", value: "createdBy", sortable: false, width: "20%" },
        { text: "Actions", value: "actions", sortable: false, width: "10%", align: "center" }
      ];
    },
    headersMobile() {
      return [
        { text: "Name", value: "name", sortable: false, width: "50%" },
        { text: "Created At", value: "createdAt", sortable: false, width: "50%" }
      ];
    },
    rowResultHeaders() {
      return [
        { text: "#", value: "rowNumber", sortable: true, width: "50px" },
        { text: "Shop", value: "shopId", sortable: true, width: "70px" },
        { text: "SKU", value: "sku", sortable: true },
        { text: "Status", value: "status", sortable: true, width: "100px" },
        { text: "Selection", value: "selectionAction", sortable: true, width: "90px" },
        { text: "Categories (+/-/=)", value: "categories", sortable: false, width: "150px" },
        { text: "Message", value: "message", sortable: false }
      ];
    }
  },
  watch: {
    pagination: {
      handler() {
        if (this.apiInitialized) {
          this.retrieveSelectionGroups();
        }
      },
      deep: true
    }
  },
  async mounted() {
    await TopProductsApiService.init();
    this.apiInitialized = true;
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.pageName }]);
    await this.retrieveSelectionGroups();
  },
  methods: {
    isMobile() {
      return window.innerWidth < 768;
    },

    rowStatusColor(status) {
      if (status === "matched") return "success";
      if (status === "unmatched") return "warning";
      if (status === "error") return "error";
      return "grey";
    },

    rowStatusCount(status) {
      if (!this.importResult || !this.importResult.rowResults) return 0;
      return this.importResult.rowResults.filter(r => r.status === status).length;
    },

    getRequestParams() {
      return {
        page: this.pagination.page - 1,
        size: this.pagination.itemsPerPage
      };
    },

    async retrieveSelectionGroups() {
      try {
        this.loading = true;
        const params = this.getRequestParams();
        const response = await TopProductsApiService.getAllItemSelectionGroups(params);

        this.selectionGroups = response.data.content || [];
        this.totalGroups = response.data.totalElements || 0;
        this.totalPages = response.data.totalPages || 0;

        this.$store.dispatch(SET_BREADCRUMB, [{ title: `${this.pageName}: ${this.totalGroups}` }]);
      } catch (error) {
        console.error("Error fetching selection groups:", error);
        this.$bvToast?.toast("Failed to load selection groups", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    openCreateDialog() {
      this.formData = {
        id: null,
        name: ""
      };
      this.createDialog = true;
    },

    closeCreateDialog() {
      this.createDialog = false;
      this.formData = {
        id: null,
        name: ""
      };
    },

    async createGroup() {
      if (!this.$refs.createForm.validate()) return;

      try {
        this.loading = true;
        await TopProductsApiService.createItemSelectionGroup({
          name: this.formData.name
        });

        this.$bvToast?.toast("Selection group created successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });

        this.closeCreateDialog();
        await this.retrieveSelectionGroups();
      } catch (error) {
        console.error("Error creating selection group:", error);
        this.$bvToast?.toast("Failed to create selection group", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    openEditDialog(group) {
      this.formData = {
        id: group.id,
        name: group.name
      };
      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
      this.formData = {
        id: null,
        name: ""
      };
    },

    async updateGroup() {
      if (!this.$refs.editForm.validate()) return;

      try {
        this.loading = true;
        await TopProductsApiService.updateItemSelectionGroup(this.formData.id, {
          name: this.formData.name
        });

        this.$bvToast?.toast("Selection group updated successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });

        this.closeEditDialog();
        await this.retrieveSelectionGroups();
      } catch (error) {
        console.error("Error updating selection group:", error);
        this.$bvToast?.toast("Failed to update selection group", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    openDeleteDialog(group) {
      this.groupToDelete = group;
      this.confirmDeleteDialog = true;
    },

    async exportGroup(group) {
      try {
        this.loading = true;
        const response = await TopProductsApiService.exportSelectionGroup(group.id);
        const blob = new Blob([response.data], { type: "text/tab-separated-values" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `selection-group-${group.id}.tsv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting selection group:", error);
        this.$bvToast?.toast("Failed to export selection group", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    async exportCategories() {
      try {
        this.loading = true;
        const response = await TopProductsApiService.exportCategories();
        const blob = new Blob([response.data], { type: "text/tab-separated-values" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "categories.tsv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting categories:", error);
        this.$bvToast?.toast("Failed to export categories", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    openImportDialog(group) {
      this.importGroup = group;
      this.importFile = null;
      this.importMode = "append";
      this.importResult = null;
      this.importDialog = true;
    },

    closeImportDialog() {
      this.importDialog = false;
      this.importGroup = null;
      this.importFile = null;
      this.importResult = null;
    },

    async runImport() {
      if (!this.importFile || !this.importGroup) return;
      try {
        this.importing = true;
        this.importResult = null;
        const text = await this.importFile.text();
        const response = await TopProductsApiService.importSelectionGroup(
          this.importGroup.id,
          this.importMode,
          text
        );
        this.importResult = response.data;
      } catch (error) {
        console.error("Error importing selection:", error);
        this.$bvToast?.toast("Failed to import selection", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.importing = false;
      }
    },

    async confirmDelete() {
      if (!this.groupToDelete) return;

      try {
        this.loading = true;
        await TopProductsApiService.deleteItemSelectionGroup(this.groupToDelete.id);

        this.$bvToast?.toast("Selection group deleted successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });

        this.confirmDeleteDialog = false;
        this.groupToDelete = null;
        await this.retrieveSelectionGroups();
      } catch (error) {
        console.error("Error deleting selection group:", error);
        this.$bvToast?.toast("Failed to delete selection group", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
